import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrigamiHttpResponse, ServiceBase } from './base/service.base';
import { OrigamiResponse } from '@app/origami-api/services/base/service.base';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { TeamGroupType } from '@app/origami-api/services/teams.service';

export namespace UserGroupServiceOptions {
  export interface GetUserGroupsOptions {
    siteId?: number;
    userName: string;
    groupTypes?: number[];
    searchClause?: string;
  }

  export interface GetDistinctGroupsOptions {
    groupTypes: number[];
    roomIds: number[];
    searchClause: string;
    start: number;
  }

  export interface GetSiteGroupsOptions {
    groupTypes?: number[];
    siteId: number;
    includeAclGroups?: boolean;
    actionsFilter?: number[];
    displayName?: string;
    groupIds?: string;
    orderByClause?: string;
    start?: number;
    limit?: number;
    parentGroupId?: number;
    rootLevelGroupsOnly?: number;
    investigativeSiteId?: number;
    getExtendedInfo?: boolean;
    isSystemGroup?: boolean;
  }
  // not sure
  export interface GetSiteGroupsConfig {
    groupTypes: number[];
    siteId: number;
    includeAllGroups?: boolean;
    includeAclGroups?: boolean;
    includeStudyStartupTeamGroups?: boolean;
    includeStudyStartupTeamRoomGroups?: boolean;
    includeInvestigativeSiteInDataRoomGroups?: boolean;
    includeSecurityGroups?: boolean;
    includeSmGroups?: boolean;
    includeWorkflowGroups?: boolean;
    includeInvestigativeSiteGroups?: boolean;
    includeContactGroups?: boolean;
    includeSponsorGroups?: boolean;
    includeISReaders?: boolean;
    includeStartupSpecialists?: boolean;
    includeISEditors?: boolean;
    includeCountryReaders?: boolean;
    includeCountryEditors?: boolean;
    includeUserLookup?: boolean;
    includeIndexManager?: boolean;
    includeMobileAccess?: boolean;
    includeAdministrativeWorkflow?: boolean;
    includeQueryManager?: boolean;
    includeGeneralQueryResponder?: boolean;
    includeISProtectedDocs?: boolean;
    includeISOthers?: boolean;
    includeISCRA?: boolean;
    includeTIAnalytics?: boolean;
    includeOrganizationManagement?: boolean;
    actionsFilter?: boolean;
    searchClause?: string;
    orderByClause?: string;
    start?: number;
    limit?: number;
  }

  export interface GetAclSiteGroupsOptions {
    maxRoleLevel: number;
    minRoleLevel: number;
    siteId: number;
  }

  export interface GetGroupInfoOptions {
    groupName: string;
    siteId: number;
  }

  export interface EditUsersAclGroupOptions {
    groupNames: string;
    siteId: number;
    userNames: string;
    forceDelete?: boolean;
    reason?: string;
  }

  export interface AddUsersToGroupsOptions {
    groupNames: string;
    removePrevious: boolean;
    removePreviousGroupTypeIds: number[] | null;
    siteId: number;
    userNames: string;
  }

  export interface CreateUserGroupOptions {
    actionIds: number[];
    cloneSecurityFromGroupName: boolean;
    group: { DisplayName: string; Description: string };
    siteId: number;
  }

  export interface ModifyUserGroupOptions {
    actionIds: number[];
    existingGroupName: number;
    group: { DisplayName: string; Description: string };
    siteId: number;
  }

  export interface GetChildGroupsOptions {
    parentGroupId: string | number;
    siteId: number;
  }

  export interface SetChildGroupsOptions {
    childGroupNames: string[];
    siteId: number;
    groupName: string;
  }

  export interface DeleteUserGroupOptions {
    groupName: string;
    siteId: number;
  }

  export interface RemoveChildGroupsOptions {
    siteId: number;
    groupName: string;
    childGroupNames: string[];
  }

  export interface UsersFromGroup {
    groupName: string;
    userNames: string;
    siteId: number;
    comments?: string;
  }
}

export namespace UserGroupServiceModel {
  export interface GetGroupsResult {
    Records: SiteGroup[];
    TotalRowCount: number;
  }

  export interface ChildGroup {
    CreatedBy: number;
    CreatedDate: string;
    Description: string;
    DisplayName: string;
    DomainId: number;
    GroupId: number;
    GroupName: string;
    GroupTypeId: number;
    IsSystemGroup: boolean;
    LastUpdated: string;
    LastUpdatedBy: number;
    StatusId: number;
  }

  export interface SiteGroup {
    Actions?: any[];
    ChildGroups?: SiteGroup[];
    Description?: string;
    DisplayName: string;
    GroupId: number | string;
    GroupName: string;
    GroupTypeId: number;
    IsSystemGroup?: boolean;
    ParentGroupId?: number;
    RoleLevel?: number;
    Index?: number;
    Teams?: { Name: string; TeamId: number; Type: TeamGroupType }[];
  }

  export interface UserGroup {
    Description: string;
    DisplayName: string;
    GroupId: number;
    GroupName: string;
    GroupTypeId: number;
    IsSystemGroup: boolean;
  }
}

@Injectable()
export class UserGroupService extends ServiceBase {
  serviceUrl = 'UserGroupService.svc';

  getUserGroups(
    options: UserGroupServiceOptions.GetUserGroupsOptions,
    ...params
  ): Observable<OrigamiResponse<UserGroupServiceModel.UserGroup[]>> {
    return this.origamiPost(
      '/GetUserGroups',
      {
        siteId: options.siteId,
        userName: options.userName,
        groupTypes: options.groupTypes ? options.groupTypes.join(',') : null,
      },
      ...params,
    );
  }

  getDistinctGroups(
    options: UserGroupServiceOptions.GetDistinctGroupsOptions,
    ...params
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/GetDistinctGroups', options, ...params);
  }

  getSiteGroups(
    options: UserGroupServiceOptions.GetSiteGroupsOptions,
    ...params
  ): Observable<OrigamiResponse<UserGroupServiceModel.GetGroupsResult>> {
    return this.origamiPost('/GetSiteGroups', options, ...params);
  }

  getAclSiteGroups(
    options: UserGroupServiceOptions.GetAclSiteGroupsOptions,
    ...params
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/GetAclSiteGroups', options, ...params);
  }

  getGroupInfo(
    options: UserGroupServiceOptions.GetGroupInfoOptions,
    forceRequest?: boolean,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/GetGroupInfo', options, null, null, forceRequest);
  }

  editUsersAclGroup(
    options: UserGroupServiceOptions.EditUsersAclGroupOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/EditUsersAclGroup', options, null, silentErrorCodes, true);
  }

  addUsersToGroups(options: UserGroupServiceOptions.AddUsersToGroupsOptions): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/AddUsersToGroups', options, null, null, true);
  }

  createUserGroup(options: UserGroupServiceOptions.CreateUserGroupOptions): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/CreateUserGroup', options, null, null, true);
  }

  modifyUserGroup(options: UserGroupServiceOptions.ModifyUserGroupOptions): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ModifyUserGroup', options, null, null, true);
  }

  setChildGroups(
    options: UserGroupServiceOptions.SetChildGroupsOptions,
    silentErrorCodes: ErrorCode[] = [],
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/SetChildGroups', options, null, silentErrorCodes, true);
  }

  getChildGroups(
    options: UserGroupServiceOptions.GetChildGroupsOptions,
    ...params
  ): Observable<OrigamiResponse<UserGroupServiceModel.ChildGroup[]>> {
    return this.origamiPost('/GetChildGroups', options, ...params);
  }

  deleteUserGroup(
    options: UserGroupServiceOptions.DeleteUserGroupOptions,
    silentErrorCodes: number[] = null,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/DeleteUserGroup', options, null, silentErrorCodes, true);
  }

  removeChildGroups(options: UserGroupServiceOptions.RemoveChildGroupsOptions): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/RemoveChildGroups', options, null, null, true);
  }

  removeUsersFromGroup(options: UserGroupServiceOptions.UsersFromGroup): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/RemoveUsersFromGroup', options, null, null, true);
  }

  addUserToGroup(options: UserGroupServiceOptions.UsersFromGroup): Observable<OrigamiResponse<null>> {
    return this.origamiPost('/AddUserToGroup', options, null, null, true);
  }
}
